import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { HeartHandshake, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const STRIPE_BADGE = {
  active: { label: "Stripe live", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "Stripe onboarding", cls: "bg-amber-100 text-amber-700" },
  blocked: { label: "Stripe disabled", cls: "bg-rose-100 text-rose-700" },
  not_connected: { label: "Stripe not connected", cls: "bg-slate-100 text-slate-600" },
};

// OCI V2.20 — Beneficiary charity selector, front-and-centre in the campaign edit flow.
// One action sets BOTH campaign.charityId (donor visibility) and the canonical
// campaign.beneficiaryOrganisationId (payment) via /campaigns/{id}/beneficiary.
export default function CampaignBeneficiarySelector({ campaignId, canEdit = true }) {
  const [charities, setCharities] = useState([]);
  const [current, setCurrent] = useState(null); // charityId
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      setLoading(true);
      try {
        const [listRes, campRes] = await Promise.all([
          apiClient.get("/agency/beneficiary-charities"),
          apiClient.get(`/campaigns/${campaignId}/beneficiary`),
        ]);
        setCharities(listRes.data?.charities || []);
        setCurrent(campRes.data?.charityId || null);
      } catch (e) {
        // beneficiary list is owner-only; managers will get 403 — hide gracefully
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  const assign = async (charityId) => {
    setSaving(true);
    try {
      const value = charityId === "__none__" ? null : charityId;
      await apiClient.put(`/campaigns/${campaignId}/beneficiary`, { charityId: value });
      setCurrent(value);
      toast.success(value ? "Beneficiary charity assigned" : "Beneficiary cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign beneficiary");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm py-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading charities…</div>;
  }

  const selected = charities.find((c) => c.charityId === current);
  const badge = selected ? (STRIPE_BADGE[selected.stripeState] || STRIPE_BADGE.not_connected) : null;

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="campaign-beneficiary-selector">
      <div className="flex items-center gap-2 mb-1">
        <HeartHandshake className="h-5 w-5 text-indigo-600" />
        <h3 className="font-semibold text-slate-900">Beneficiary charity</h3>
        {!current && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Recommended</Badge>}
      </div>
      <p className="text-sm text-slate-500 mb-3">
        The charity that receives donor submissions and donor records from this campaign.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={current || "__none__"} onValueChange={assign} disabled={!canEdit || saving}>
          <SelectTrigger className="w-72 bg-white" data-testid="beneficiary-charity-select">
            <SelectValue placeholder="Select a beneficiary charity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" data-testid="beneficiary-option-none">No beneficiary assigned</SelectItem>
            {charities.map((c) => (
              <SelectItem key={c.charityId} value={c.charityId} data-testid={`beneficiary-option-${c.charityId}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        {badge && (
          <Badge className={`${badge.cls} hover:${badge.cls} gap-1`} data-testid="beneficiary-stripe-state">
            {selected.stripeState === "active" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {badge.label}
          </Badge>
        )}
      </div>

      {selected && (
        <p className="mt-3 text-sm text-slate-600 flex items-start gap-2" data-testid="beneficiary-statement-only-note">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-emerald-600" />
          Donor submissions on this campaign are recorded securely for {selected.name} and appear in their charity portal automatically. No payment provider is required to receive donor submissions.
        </p>
      )}
    </div>
  );
}
